import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminPageHeader, AdminSubTabs, useAdminQueryParam } from "@/pages/admin/AdminUx";
import { MACBOOK_PROMO_STEPS, DEFAULT_AUTOMATION_STEPS } from "@/lib/siteContent";
import { fieldUsage } from "@/pages/admin/cmsFieldMeta";
import { CmsSplitPreview } from "@/pages/admin/CmsLivePreview";

const HOMEPAGE_SECTIONS = [
  { id: "hero", label: "Hero" },
  { id: "macbook", label: "MacBook" },
  { id: "about", label: "About" },
  { id: "automation", label: "Automation" },
  { id: "ai", label: "AI Platform" },
  { id: "contact", label: "Contact" },
  { id: "legacy", label: "Legacy" },
];

const HERO_FIELDS = [
  { key: "main_hero_en_line1", label: "영문 제목 1줄" },
  { key: "main_hero_en_line2_before", label: "영문 2줄 앞" },
  { key: "main_hero_en_line2_highlight", label: "영문 2줄 강조" },
  { key: "main_hero_kr_line1", label: "한글 제목 1줄" },
  { key: "main_hero_kr_line2_highlight", label: "한글 강조" },
  { key: "main_hero_kr_line2_after", label: "한글 강조 뒤" },
  { key: "main_hero_en_tagline", label: "영문 태그라인 (쉼표 구분)" },
  { key: "main_hero_kr_tagline", label: "한글 태그라인 (쉼표 구분)" },
  { key: "main_hero_en_cta_lead", label: "영문 CTA 리드" },
  { key: "main_hero_en_cta_label", label: "영문 CTA 버튼" },
  { key: "main_hero_kr_cta_lead", label: "한글 CTA 리드" },
  { key: "main_hero_kr_cta_label", label: "한글 CTA 버튼" },
  { key: "main_hero_cta_url", label: "CTA 링크" },
];

const LEGACY_FIELDS = [
  { key: "hero_badge_text", label: "Legacy Hero 뱃지" },
  { key: "hero_title", label: "Legacy Hero 제목", type: "textarea" },
  { key: "hero_subtitle", label: "Legacy Hero 설명", type: "textarea" },
  { key: "cta_primary_text", label: "Legacy 주 CTA" },
  { key: "cta_primary_link", label: "Legacy 주 CTA 링크" },
  { key: "cta_secondary_text", label: "Legacy 보조 CTA" },
  { key: "cta_secondary_link", label: "Legacy 보조 CTA 링크" },
  { key: "services_title", label: "Legacy Services 제목" },
  { key: "services_body", label: "Legacy Services 설명", type: "textarea" },
  { key: "portfolio_title", label: "Legacy Portfolio 섹션 제목" },
  { key: "process_title", label: "Legacy Process 제목" },
];

const SITE_FIELDS = [
  { key: "company_name", label: "회사명" },
  { key: "company_ceo", label: "대표자" },
  { key: "company_business_no", label: "사업자등록번호" },
  { key: "company_email", label: "대표 이메일" },
  { key: "company_phone", label: "대표 전화번호" },
  { key: "company_address", label: "회사 주소", type: "textarea" },
  { key: "kakao_channel_url", label: "카카오 채널 URL" },
  { key: "footer_copyright", label: "푸터 저작권 문구" },
];

const inputClass =
  "w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#0055FF]";

function Field({ label, children, span2, hint }) {
  return (
    <div className={span2 ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5 block">{label}</label>
      {children}
      {hint ? <p className="mt-1.5 text-[11px] leading-relaxed text-white/40">{hint}</p> : null}
    </div>
  );
}

function TextField({ data, setData, field }) {
  const common = {
    value: data[field.key] ?? "",
    onChange: (e) => setData({ ...data, [field.key]: e.target.value }),
    "data-testid": `cms-${field.key}`,
    className: inputClass,
  };
  if (field.type === "textarea") return <textarea rows={3} {...common} />;
  return <input {...common} />;
}

export default function CmsTab({ category = "cms", onSaved }) {
  const isSite = category === "site";
  const [cmsView, setCmsView] = useState(() => {
    try {
      return localStorage.getItem("dsite-cms-view") === "dev" ? "dev" : "simple";
    } catch {
      return "simple";
    }
  });
  const setView = (next) => {
    setCmsView(next);
    try {
      localStorage.setItem("dsite-cms-view", next);
    } catch {
      /* ignore */
    }
  };
  const sections = isSite
    ? [
        { id: "basic", label: "기본 정보" },
        { id: "contact", label: "연락처" },
        { id: "history", label: "변경 이력" },
      ]
    : [
        ...HOMEPAGE_SECTIONS.filter((s) => cmsView === "dev" || s.id !== "legacy"),
        { id: "history", label: "변경 이력" },
      ];
  const [section, setSection] = useAdminQueryParam("section", isSite ? "basic" : "hero");
  const active = sections.find((s) => s.id === section) || sections[0];
  const [data, setData] = useState({});
  const [savedSnap, setSavedSnap] = useState("{}");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedNote, setSavedNote] = useState("");

  const macbookSteps = useMemo(() => {
    const rows = Array.isArray(data.macbook_steps) ? data.macbook_steps : [];
    return MACBOOK_PROMO_STEPS.map((fallback, i) => ({
      eyebrow: rows[i]?.eyebrow ?? fallback.eyebrow,
      title: Array.isArray(rows[i]?.title) ? rows[i].title.join("\n") : (rows[i]?.title ?? fallback.title.join("\n")),
      body: rows[i]?.body ?? fallback.body,
    }));
  }, [data.macbook_steps]);

  const automationSteps = useMemo(() => {
    const rows = Array.isArray(data.automation_steps) ? data.automation_steps : [];
    return DEFAULT_AUTOMATION_STEPS.map((fallback, i) => ({
      label: rows[i]?.label ?? fallback.label,
      desc: rows[i]?.desc ?? fallback.desc,
    }));
  }, [data.automation_steps]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([api.get("/cms"), api.get("/admin/cms/history")]);
      setData(c.data || {});
      setSavedSnap(JSON.stringify(c.data || {}));
      setHistory(h.data || []);
    } catch {
      toast.error("로드 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [category]);

  useEffect(() => {
    if (!isSite && cmsView !== "dev" && section === "legacy") {
      setSection("hero");
    }
  }, [cmsView, isSite, section, setSection]);

  const ownedKeys = () => {
    if (isSite) return SITE_FIELDS.map((f) => f.key);
    return [
      ...HERO_FIELDS.map((f) => f.key),
      "macbook_steps",
      "about_title",
      "about_body",
      "automation_eyebrow",
      "automation_title",
      "automation_description",
      "automation_steps",
      "ai_platform_title",
      "ai_platform_body",
      "contact_eyebrow",
      "contact_title",
      "contact_description",
      ...LEGACY_FIELDS.map((f) => f.key),
    ];
  };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {};
      ownedKeys().forEach((k) => {
        payload[k] = extra[k] !== undefined ? extra[k] : data[k] ?? "";
      });
      await api.put("/admin/cms", payload);
      toast.success("저장 완료 · DEV 홈페이지 반영됨");
      setSavedNote("저장 완료 · DEV 홈페이지 반영됨");
      if (onSaved) onSaved();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  const updateMacbook = (index, key, value) => {
    const next = macbookSteps.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    setData({
      ...data,
      macbook_steps: next.map((row) => ({
        eyebrow: row.eyebrow,
        title: String(row.title || "").split("\n"),
        body: row.body,
      })),
    });
  };

  const updateAutoStep = (index, key, value) => {
    const next = automationSteps.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    setData({ ...data, automation_steps: next });
  };

  const siteVisible = isSite
    ? SITE_FIELDS.filter((f) => {
        if (active.id === "basic") return ["company_name", "company_ceo", "company_business_no", "footer_copyright"].includes(f.key);
        if (active.id === "contact") return ["company_email", "company_phone", "company_address", "kakao_channel_url"].includes(f.key);
        return false;
      })
    : [];

  const dirty = JSON.stringify(data) !== savedSnap;
  const previewSection = isSite ? "footer" : (active?.id === "history" ? "hero" : active?.id);
  const viewToggle = !isSite ? (
    <div className="flex gap-2 mt-3" role="group" aria-label="보기 모드" data-testid="cms-view-mode">
      <button
        type="button"
        className={cmsView === "simple" ? "btn-primary" : "admin-row__btn"}
        onClick={() => setView("simple")}
      >
        일반 보기
      </button>
      <button
        type="button"
        className={cmsView === "dev" ? "btn-primary" : "admin-row__btn"}
        onClick={() => setView("dev")}
        data-testid="cms-dev-view"
      >
        개발자 보기
      </button>
    </div>
  ) : null;

  useEffect(() => {
    const onLeave = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  const editor = (
    <>
      <AdminSubTabs
        tabs={sections.map((s) => ({ id: s.id, label: s.label }))}
        value={active?.id}
        onChange={setSection}
      />

      {active?.id === "hero" && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 gap-4">
          {HERO_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} hint={fieldUsage(f.key)}>
              <TextField data={data} setData={setData} field={f} />
            </Field>
          ))}
        </div>
      )}

      {active?.id === "macbook" && (
        <div className="space-y-3">
          <p className="text-xs text-white/40">3D 모델·스크롤 애니메이션은 코드 유지. 카피 5스텝만 수정합니다.</p>
          {macbookSteps.map((step, i) => (
            <div key={i} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 grid grid-cols-1 gap-3">
              <Field label={`Step ${i + 1} 라벨`} hint="사용 위치: 메인 홈페이지 > MacBook > 스텝 카피">
                <input className={inputClass} value={step.eyebrow} onChange={(e) => updateMacbook(i, "eyebrow", e.target.value)} data-testid={`cms-macbook-${i}-eyebrow`} />
              </Field>
              <Field label="본문">
                <input className={inputClass} value={step.body} onChange={(e) => updateMacbook(i, "body", e.target.value)} data-testid={`cms-macbook-${i}-body`} />
              </Field>
              <Field label="제목 (줄바꿈 = 라인)" span2>
                <textarea className={inputClass} rows={3} value={step.title} onChange={(e) => updateMacbook(i, "title", e.target.value)} data-testid={`cms-macbook-${i}-title`} />
              </Field>
            </div>
          ))}
        </div>
      )}

      {active?.id === "about" && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 gap-4">
          <Field label="About 제목" hint={fieldUsage("about_title")}>
            <TextField data={data} setData={setData} field={{ key: "about_title", type: "textarea" }} />
          </Field>
          <Field label="About 본문" hint={fieldUsage("about_body")}>
            <TextField data={data} setData={setData} field={{ key: "about_body", type: "textarea" }} />
          </Field>
        </div>
      )}

      {active?.id === "automation" && (
        <div className="space-y-3">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 gap-4">
            <Field label="상단 라벨" hint={fieldUsage("automation_eyebrow")}>
              <TextField data={data} setData={setData} field={{ key: "automation_eyebrow" }} />
            </Field>
            <Field label="제목" hint={fieldUsage("automation_title")}>
              <TextField data={data} setData={setData} field={{ key: "automation_title", type: "textarea" }} />
            </Field>
            <Field label="설명" hint={fieldUsage("automation_description")}>
              <TextField data={data} setData={setData} field={{ key: "automation_description", type: "textarea" }} />
            </Field>
          </div>
          {automationSteps.map((step, i) => (
            <div key={i} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 grid grid-cols-1 gap-3">
              <Field label={`Step ${i + 1} 이름`} hint="사용 위치: 메인 홈페이지 > Automation > 흐름 스텝">
                <input className={inputClass} value={step.label} onChange={(e) => updateAutoStep(i, "label", e.target.value)} />
              </Field>
              <Field label="설명">
                <input className={inputClass} value={step.desc} onChange={(e) => updateAutoStep(i, "desc", e.target.value)} />
              </Field>
            </div>
          ))}
        </div>
      )}

      {active?.id === "ai" && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 gap-4">
          <Field label="제목" hint={fieldUsage("ai_platform_title")}>
            <TextField data={data} setData={setData} field={{ key: "ai_platform_title" }} />
          </Field>
          <Field label="설명" hint={fieldUsage("ai_platform_body")}>
            <TextField data={data} setData={setData} field={{ key: "ai_platform_body", type: "textarea" }} />
          </Field>
        </div>
      )}

      {active?.id === "contact" && !isSite && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 gap-4">
          <p className="text-xs text-white/40">문의 폼 필드/검증/API는 변경하지 않습니다. 헤드라인만 수정합니다.</p>
          <Field label="상단 라벨" hint={fieldUsage("contact_eyebrow")}>
            <TextField data={data} setData={setData} field={{ key: "contact_eyebrow" }} />
          </Field>
          <Field label="제목 (줄바꿈 = 2줄)" hint={fieldUsage("contact_title")}>
            <TextField data={data} setData={setData} field={{ key: "contact_title", type: "textarea" }} />
          </Field>
          <Field label="설명" hint={fieldUsage("contact_description")}>
            <TextField data={data} setData={setData} field={{ key: "contact_description", type: "textarea" }} />
          </Field>
        </div>
      )}

      {active?.id === "legacy" && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 gap-4">
          <p className="text-xs text-amber-200/80">
            현재 사이트에서 사용하지 않는 이전 설정입니다. DB 값은 유지되며, 공개 화면에는 연결하지 않습니다.
          </p>
          {LEGACY_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} span2={f.type === "textarea"}>
              <TextField data={data} setData={setData} field={f} />
            </Field>
          ))}
        </div>
      )}

      {isSite && active?.id !== "history" && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 gap-4">
          {siteVisible.map((f) => (
            <Field key={f.key} label={f.label} span2={f.type === "textarea"} hint={fieldUsage(f.key)}>
              <TextField data={data} setData={setData} field={f} />
            </Field>
          ))}
        </div>
      )}

      {active?.id === "history" && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <h4 className="font-display text-base mb-4 text-white">변경 이력 ({history.length})</h4>
          {history.length === 0 ? (
            <div className="text-xs text-white/40">이력이 없습니다.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {history.slice(0, 40).map((h, i) => (
                <div key={i} className="border border-white/5 rounded-lg p-3 text-xs">
                  <div className="flex justify-between text-white/70">
                    <span>{h.actor}</span>
                    <span className="text-white/40">{new Date(h.created_at).toLocaleString("ko-KR")}</span>
                  </div>
                  <div className="mt-1 text-white/50">변경: {Object.keys(h.changes || {}).join(", ")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {active?.id !== "history" && (
        <div className="admin-sticky-actions">
          <button onClick={() => save()} disabled={saving || loading} className="btn-primary disabled:opacity-50">
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      )}
    </>
  );

  if (active?.id === "history") {
    return (
      <div className="space-y-4" data-testid={`admin-${category}`}>
        <AdminPageHeader
          title={isSite ? "사이트 정보" : "홈페이지 콘텐츠"}
          description="변경 이력만 표시합니다."
          extra={viewToggle}
        />
        {editor}
      </div>
    );
  }

  return (
    <CmsSplitPreview
      testId={`admin-${category}`}
      cms={data}
      section={previewSection}
      dirty={dirty}
      savedNote={savedNote}
      header={(
        <AdminPageHeader
          title={isSite ? "사이트 정보" : "홈페이지 콘텐츠"}
          description={
            isSite
              ? "왼쪽에서 수정하면 오른쪽 Footer 미리보기에 바로 반영됩니다. 저장 전에는 DEV DB에 쓰지 않습니다."
              : "왼쪽 입력은 저장 전에 오른쪽 실제 섹션 미리보기에만 반영됩니다."
          }
          extra={viewToggle}
          actions={(
            <button
              onClick={() => save()}
              disabled={saving || loading}
              data-testid={`${category}-save`}
              className="btn-primary disabled:opacity-50"
            >
              {saving ? "저장 중..." : "저장"}
            </button>
          )}
        />
      )}
      editor={editor}
    />
  );
}
